import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Spin, Table, Tooltip } from 'antd';
import { describeOpsError, getOpsTicket, listOpsTicketsForAsset, readOpsToken } from '../../util/opsApi';
import { findLinkedIotDevice } from './linkedIotDevice';
import OpsTicketDetail, { EMPTY, formatDate, StatusTag } from './OpsTicketDetail';
import { OPS_TICKETS_CHANGED_EVENT } from './RaiseTicketButton';

/**
 * "Ops Tickets" on the Asset History Report.
 *
 * IoT tickets are raised and updated in the Ops module, not in AssetPro. This
 * shows, read-only, the Ops tickets whose assetCode is EXACTLY this asset's
 * code, plus each ticket's update timeline. See docs/ops-iot-tickets.md.
 *
 * API calls:
 *  - page load (asset with an IoT device), and Refresh:
 *    GET /setting/organisation/:id (AssetPro, for the Ops token), then
 *    GET /integration/support-tickets?assetCode= (Ops). The result drives the
 *    "Tickets (open / total)" counter and is reused when the list is opened.
 *  - opening a ticket: GET /integration/support-tickets/:id (Ops), once per
 *    ticket while the modal stays open (complaint, attachments, timeline)
 *  - attachments: GET .../attachments/:id/download (Ops) — images when the
 *    ticket opens (thumbnails), other files on click; once per file per open
 * The token lives in this component's state only.
 */

// SupportTicketSource in Ops. An unknown value is shown as-is, not guessed.
const SOURCE_LABELS = {
  ASSETPRO: 'Raised by client (AssetPro)',
  OPS: 'Raised by AssetPro team (Ops)',
};

const pill = { display: 'inline-flex', borderRadius: 11, overflow: 'hidden', fontSize: 12, lineHeight: '20px', border: '1px solid #e8e8e8' };
const segment = (bg, color) => ({ padding: '0 9px', background: bg, color, whiteSpace: 'nowrap' });

/** Two-part pill: "● 2 Open | 5 Total" (open segment amber when > 0, green when 0). */
function TicketCounter({ loading, failed, tickets, counts }) {
  if (loading || (!tickets && !failed)) return <Spin size="small" />;
  if (failed) {
    return <span style={{ ...pill, borderColor: '#ffccc7' }}><span style={segment('#fff1f0', '#cf1322')}>Unavailable</span></span>;
  }
  const hasOpen = counts.open > 0;
  return (
    <span style={pill}>
      <span style={{ ...segment(hasOpen ? '#fff7e6' : '#f6ffed', hasOpen ? '#d46b08' : '#389e0d'), fontWeight: 600 }}>
        <span style={{
          display: 'inline-block', width: 6, height: 6, borderRadius: '50%', marginRight: 6, verticalAlign: 'middle',
          background: hasOpen ? '#fa8c16' : '#52c41a',
        }} />
        {counts.open} Open
      </span>
      {counts.unknown > 0 && <span style={{ ...segment('#fffbe6', '#ad8b00'), borderLeft: '1px solid #e8e8e8' }}>{counts.unknown} Unknown</span>}
      <span style={{ ...segment('#fafafa', '#595959'), borderLeft: '1px solid #e8e8e8' }}>{tickets.length} Total</span>
    </span>
  );
}

export default function OpsTicketsButton({ data, authUser }) {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tickets, setTickets] = useState(null); // null = not loaded yet
  const [token, setToken] = useState(null);
  const [details, setDetails] = useState({}); // ticketId -> { loading, data, error }
  const [selectedId, setSelectedId] = useState(null); // ticket open in detail view

  const asset = data && data.asset;
  const hasIotDevice = !!(asset && findLinkedIotDevice(data));
  const assetCode = asset && asset.assetCode;
  const assetId = asset && asset._id;

  const loadTickets = async () => {
    setLoading(true);
    setError(null);
    setTickets(null);
    setDetails({});
    setSelectedId(null);
    try {
      if (!assetCode) {
        setError('This asset has no asset code, so its Ops tickets cannot be looked up.');
        return;
      }
      const orgId = authUser && authUser.organisation && authUser.organisation._id;
      if (!orgId) {
        setError('Organisation of the logged-in user is not known, so the Ops token cannot be read.');
        return;
      }
      const tokenResult = await readOpsToken(orgId);
      if (tokenResult.error) {
        setError(tokenResult.error);
        return;
      }
      setToken(tokenResult.token);
      const response = await listOpsTicketsForAsset(tokenResult.token, assetCode);
      if (!Array.isArray(response.data)) {
        setError('Ops returned an unexpected response for the ticket list.');
        return;
      }
      setTickets(response.data);
    } catch (e) {
      setError(describeOpsError(e));
    } finally {
      setLoading(false);
    }
  };

  // Load once per asset so the counter is visible without opening the list.
  useEffect(() => {
    if (hasIotDevice) loadTickets();
    // loadTickets is recreated every render; re-run only when the asset changes.
  }, [assetId, hasIotDevice]);

  // A ticket raised via "Raise Ticket" on this page refreshes the counter.
  useEffect(() => {
    const onChanged = (e) => {
      if (hasIotDevice && e.detail && e.detail.assetCode === assetCode) loadTickets();
    };
    window.addEventListener(OPS_TICKETS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(OPS_TICKETS_CHANGED_EVENT, onChanged);
  }, [assetCode, hasIotDevice]);

  const open = () => {
    setVisible(true);
    if (!tickets && !loading) loadTickets(); // e.g. after an earlier failure
  };

  const close = () => {
    // Tickets and token stay loaded for the counter; only the view resets.
    setVisible(false);
    setSelectedId(null);
  };

  const openTicket = (ticketId) => {
    setSelectedId(ticketId);
    loadDetail(ticketId);
  };

  const loadDetail = async (ticketId) => {
    if (details[ticketId] && !details[ticketId].error) return; // already loaded or loading
    setDetails(prev => ({ ...prev, [ticketId]: { loading: true } }));
    try {
      const response = await getOpsTicket(token, ticketId);
      setDetails(prev => ({ ...prev, [ticketId]: { data: response.data } }));
    } catch (e) {
      setDetails(prev => ({ ...prev, [ticketId]: { error: describeOpsError(e) } }));
    }
  };

  const columns = [
    { title: 'Ticket No', dataIndex: 'ticketNo', key: 'ticketNo', render: v => <a>{v || EMPTY}</a> },
    {
      title: 'Details', dataIndex: 'detailsOfComplaint', key: 'detailsOfComplaint',
      render: v => v || EMPTY,
    },
    { title: 'Status', dataIndex: 'status', key: 'status', render: s => <StatusTag status={s} /> },
    { title: 'Raised By', dataIndex: 'source', key: 'source', render: v => (v ? SOURCE_LABELS[v] || v : EMPTY) },
    {
      title: 'Assigned To', dataIndex: 'assignedTo', key: 'assignedTo',
      render: v => (v && v.name) || EMPTY,
    },
    { title: 'Complaint Date', dataIndex: 'complaintDate', key: 'complaintDate', render: v => formatDate(v) },
    { title: 'Target Date', dataIndex: 'targetDate', key: 'targetDate', render: v => formatDate(v) },
    { title: 'Last Updated', dataIndex: 'updatedAt', key: 'updatedAt', render: v => formatDate(v, true) },
    { title: 'Closed At', dataIndex: 'closedAt', key: 'closedAt', render: v => formatDate(v, true) },
  ];

  if (!hasIotDevice) return null;

  // Open = any status whose Ops category is not CLOSED (Closed and Cancelled are
  // both CLOSED). A ticket without a status category is counted as unknown, never
  // guessed into either bucket.
  const counts = (tickets || []).reduce((acc, t) => {
    const category = t.status && t.status.category;
    if (!category) acc.unknown += 1;
    else if (category !== 'CLOSED') acc.open += 1;
    return acc;
  }, { open: 0, unknown: 0 });

  const tooltip = error && !tickets
    ? error
    : counts.unknown
      ? `${counts.unknown} ticket(s) have no status category in Ops, so they are counted as neither open nor closed.`
      : 'IoT tickets raised and updated in the Ops module for this asset';

  return (
    <>
      <Tooltip placement="topLeft" title={tooltip}>
        <span onClick={open} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
          <a style={{ fontWeight: 500, marginRight: 8 }}>Tickets</a>
          <TicketCounter loading={loading && !tickets} failed={!!error && !tickets} tickets={tickets} counts={counts} />
        </span>
      </Tooltip>
      <Modal
        title={`Tickets — ${assetCode || 'asset code missing'}`}
        visible={visible}
        onCancel={close}
        width="90%"
        footer={[
          <Button key="refresh" icon="reload" onClick={loadTickets} disabled={loading}>Refresh</Button>,
          <Button key="close" type="primary" onClick={close}>Close</Button>,
        ]}
      >
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
        {!error && !selectedId && tickets && tickets.length === 0 && !loading && (
          <Alert type="info" showIcon message={`No Ops tickets found for asset code ${assetCode}.`} />
        )}
        <Spin spinning={loading}>
          {selectedId ? (
            <OpsTicketDetail key={selectedId} detail={details[selectedId]} token={token} onBack={() => setSelectedId(null)} />
          ) : tickets && tickets.length > 0 && (
            <>
              <div style={{ marginBottom: 8, color: '#8c8c8c' }}>
                {tickets.length} ticket(s). Click a ticket to open its details, attachments and timeline.
              </div>
              <Table
                rowKey="id"
                size="small"
                columns={columns}
                dataSource={tickets}
                pagination={{ pageSize: 10 }}
                scroll={{ x: true }}
                onRow={record => ({ onClick: () => openTicket(record.id), style: { cursor: 'pointer' } })}
              />
            </>
          )}
        </Spin>
      </Modal>
    </>
  );
}
